"use client"

import {
  AaveIcon,
  AdaIcon,
  ArbIcon,
  AuroraIcon,
  AvaxIcon,
  BchIcon,
  BnbIcon,
  BtcIcon,
  DogeIcon,
  EthIcon,
  LinkIcon,
  LockIcon,
  NearIcon,
  SolIcon,
  TonIcon,
  TrxIcon,
  UsdcIcon,
  UsdtIcon,
  ZecIcon,
} from "@src/icons"
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion"
import { type ComponentType, type SVGProps, useEffect, useState } from "react"

const SWAP_SLOTS: {
  id: string
  inputIcon: ComponentType<SVGProps<SVGSVGElement>>
  outputIcon: ComponentType<SVGProps<SVGSVGElement>>
}[] = [
  { id: "sol-usdt", inputIcon: SolIcon, outputIcon: UsdtIcon },
  { id: "aurora-avax", inputIcon: AuroraIcon, outputIcon: AvaxIcon },
  { id: "bnb-near", inputIcon: BnbIcon, outputIcon: NearIcon },
  { id: "eth-link", inputIcon: EthIcon, outputIcon: LinkIcon },
  { id: "btc-ada", inputIcon: BtcIcon, outputIcon: AdaIcon },
  { id: "usdc-trx", inputIcon: UsdcIcon, outputIcon: TrxIcon },
  { id: "zec-bch", inputIcon: ZecIcon, outputIcon: BchIcon },
  { id: "doge-aave", inputIcon: DogeIcon, outputIcon: AaveIcon },
  { id: "ton-arb", inputIcon: TonIcon, outputIcon: ArbIcon },
]

const ICON_COUNT = SWAP_SLOTS.length
const SPACING = 12 // degrees between icons
const TRACK_LENGTH = ICON_COUNT * SPACING // 108° total loop
const CENTER_OFFSET = ((ICON_COUNT - 1) * SPACING) / 2 // 48° — centers the arc at 0° (top)

const SPRING_CONFIG = { stiffness: 750, damping: 35, mass: 1 }

const SIZE = 1000
const BORDER_WIDTH = 4
const RADIUS = SIZE / 2 - BORDER_WIDTH / 2

function TokenIcon({
  slot,
  angle,
  isWrapping,
}: {
  slot: (typeof SWAP_SLOTS)[number]
  angle: number
  isWrapping: boolean
}) {
  const motionAngle = useMotionValue(angle)
  const springAngle = useSpring(motionAngle, SPRING_CONFIG)

  // Scale: full size at top and one position on either side, smaller elsewhere
  const isNearTop = Math.abs(angle) <= SPACING
  const targetScale = isNearTop ? 1 : 0.8
  const motionScale = useMotionValue(targetScale)
  const springScale = useSpring(motionScale, SPRING_CONFIG)

  // Combine angle + scale into one transform so they don't conflict
  const transform = useTransform(
    [springAngle, springScale],
    ([a, s]) =>
      `translate(-50%, -50%) rotate(${a}deg) translateY(-${RADIUS}px) scale(${s})`
  )

  useEffect(() => {
    if (isWrapping) {
      motionAngle.jump(angle)
      springAngle.jump(angle)
      motionScale.jump(targetScale)
      springScale.jump(targetScale)
    } else {
      motionAngle.set(angle)
      motionScale.set(targetScale)
    }
  }, [
    angle,
    isWrapping,
    motionAngle,
    springAngle,
    targetScale,
    motionScale,
    springScale,
  ])

  const Icon = angle <= 0 ? slot.inputIcon : slot.outputIcon

  return (
    <motion.div
      className="-outline-offset-1 absolute top-1/2 left-1/2 size-10 rounded-full outline outline-white/15"
      style={{ transform }}
    >
      <Icon className="size-full rounded-full" />
    </motion.div>
  )
}

const Confidential = () => {
  const [step, setStep] = useState(0)

  useEffect(() => {
    const interval = setInterval(() => {
      setStep((prev) => prev + 1)
    }, 3000)
    return () => clearInterval(interval)
  }, [])

  const targetOffset = (step * SPACING) % TRACK_LENGTH
  const wrappingIndex = (ICON_COUNT - (step % ICON_COUNT)) % ICON_COUNT

  return (
    <div
      className="-translate-x-1/2 absolute top-1/2 left-1/2"
      style={{
        height: SIZE,
        width: SIZE,
      }}
    >
      <div className="relative size-full">
        <div
          className="absolute inset-0 rounded-full border border-gray-700 bg-gray-800"
          style={{
            borderWidth: BORDER_WIDTH,
          }}
        >
          {SWAP_SLOTS.map((slot, i) => {
            const angle =
              ((i * SPACING + targetOffset) % TRACK_LENGTH) - CENTER_OFFSET

            return (
              <TokenIcon
                key={slot.id}
                slot={slot}
                angle={angle}
                isWrapping={i === wrappingIndex}
              />
            )
          })}

          <div
            className="-translate-x-1/2 -translate-y-1/2 absolute left-1/2 flex size-16 items-center justify-center rounded-2xl bg-white shadow-2xl"
            style={{
              top: -BORDER_WIDTH,
            }}
          >
            <LockIcon className="size-8 text-gray-900" />
          </div>
        </div>
      </div>
    </div>
  )
}

export default Confidential
